import useScrollAnimation from '../../hooks/useScrollAnimation';

const BRAND_STATS = [
  { value: '20+', label: 'Years in Business', sub: 'Est. 2003 — Originally Polished Concrete Images' },
  { value: '5', label: 'States Served', sub: 'MN · IA · WI · ND · SD' },
  { value: '100%', label: 'Certified Installers', sub: 'Concrete Construction Managers' },
  { value: 'Res + Com', label: 'Project Scale', sub: 'Residential & Commercial' },
];

const ACHIEVEMENTS = [
  { icon: 'ri-shield-check-fill', text: 'Founded 2003 as Polished Concrete Images — expanded and rebranded Polished Concrete Plus in 2011' },
  { icon: 'ri-map-pin-2-fill', text: 'Headquartered in Sauk Rapids, MN — strategically positioned to serve all of Minnesota and the upper Midwest' },
  { icon: 'ri-building-2-fill', text: 'Residential homes to large-scale commercial and industrial facilities throughout the region' },
  { icon: 'ri-medal-fill', text: 'Certified concrete construction managers on every project — residential to industrial scale' },
  { icon: 'ri-gem-fill', text: 'Diamond polishing, industrial coatings, terrazzo flooring, and custom embedded graphics' },
  { icon: 'ri-leaf-fill', text: 'Low-maintenance, sustainable surface solutions built to last the lifetime of the structure' },
];

const SocialProofSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { ref: ref2, isVisible: isVisible2 } = useScrollAnimation();

  return (
    <section className="relative bg-[#0a0a0a] overflow-hidden">
      {/* Full-bleed background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/ed241afc498181cdd58ffea89356af34.jpg"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/98 via-[#0a0a0a]/92 to-[#0a0a0a]/75" />
      </div>

      {/* Top divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c8a951]/35 to-transparent" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        {/* Header */}
        <div ref={ref} className={`anim-section ${isVisible ? 'is-visible' : ''} flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16`}>
          <div>
            <p className="text-[#c8a951] text-xs uppercase tracking-[0.35em] font-semibold mb-4">Our Track Record</p>
            <h2
              className="text-4xl lg:text-5xl font-bold text-white leading-tight max-w-xl"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Over Two Decades of{' '}
              <em className="text-[#c8a951] not-italic">Proven Craftsmanship</em>
            </h2>
          </div>
          <p className="text-white/40 text-sm leading-relaxed max-w-sm lg:text-right">
            Polished Concrete Plus has built its reputation on results — not promises. Two decades of certified craftsmanship in Minnesota and the upper Midwest.
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#c8a951]/12 mb-16">
          {BRAND_STATS.map((stat, i) => (
            <StatCard key={i} stat={stat} index={i} />
          ))}
        </div>

        {/* Achievements + brand statement */}
        <div
          ref={ref2}
          className={`anim-section ${isVisible2 ? 'is-visible' : ''} grid grid-cols-1 lg:grid-cols-2 gap-12 items-start`}
        >
          {/* Achievements */}
          <div>
            <h3
              className="text-xl font-bold text-white mb-8"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              What Sets Our Standard Apart
            </h3>
            <ul className="space-y-4">
              {ACHIEVEMENTS.map((item, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center border border-[#c8a951]/25 shrink-0 mt-0.5">
                    <i className={`${item.icon} text-xs text-[#c8a951]`} />
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed">{item.text}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* Brand statement panel */}
          <div className="border border-[#c8a951]/20 p-8 lg:p-10 bg-[#0d0b07]/60">
            <div
              className="text-[#c8a951]/10 font-bold leading-none select-none mb-3 -mt-3"
              style={{ fontFamily: '"Playfair Display", serif', fontSize: '120px', lineHeight: '1' }}
            >
              &ldquo;
            </div>
            <p
              className="text-lg font-semibold text-white leading-relaxed mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              We create colorful and durable interior flooring finishes and decorative concrete hardscapes — for both residential and commercial projects.
            </p>
            <p className="text-white/35 text-[10px] uppercase tracking-[0.25em] mb-8">
              — Polished Concrete Plus, since 2003
            </p>
            <div className="pt-6 border-t border-[#c8a951]/15 space-y-4">
              <p className="text-white/30 text-xs uppercase tracking-[0.18em] mb-4">Connect With Us</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.facebook.com/profile.php?id=100054372737737"
                  target="_blank"
                  rel="nofollow noopener"
                  className="flex items-center gap-2 border border-white/15 text-white/55 text-xs px-5 py-3 hover:border-[#c8a951] hover:text-[#c8a951] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-facebook-fill" /> Facebook
                </a>
                <a
                  href="https://www.instagram.com/polishedconcreteplus/"
                  target="_blank"
                  rel="nofollow noopener"
                  className="flex items-center gap-2 border border-white/15 text-white/55 text-xs px-5 py-3 hover:border-[#c8a951] hover:text-[#c8a951] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-instagram-line" /> Instagram
                </a>
              </div>
              <a
                href="tel:3204931454"
                className="inline-flex items-center gap-3 bg-[#c8a951] text-[#0a0a0a] text-xs font-bold px-8 py-4 hover:bg-[#e0c070] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill" /> 320.493.1454 — Call Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c8a951]/25 to-transparent" />
    </section>
  );
};

const StatCard = ({ stat, index }: { stat: typeof BRAND_STATS[0]; index: number }) => {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref}
      className={`anim-section ${isVisible ? 'is-visible' : ''} bg-[#0a0a0a] p-8 text-center hover:bg-[#0f0d08] transition-colors duration-300 group`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <p
        className="text-3xl lg:text-4xl font-bold text-[#c8a951] mb-2 group-hover:scale-105 transition-transform duration-300"
        style={{ fontFamily: '"Playfair Display", serif' }}
      >
        {stat.value}
      </p>
      <p className="text-white text-sm font-semibold mb-1">{stat.label}</p>
      <p className="text-white/30 text-xs leading-snug max-w-[120px] mx-auto">{stat.sub}</p>
    </div>
  );
};

export default SocialProofSection;
